"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils/cn";

type ToastVariant = "success" | "danger";

interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 4500;

/**
 * Transient success and error notifications.
 *
 * Two live regions rather than one: successes are `polite` so they wait for a screen reader to
 * finish its sentence, while failures are `assertive` because a user who just pressed Save needs
 * to know it did not happen before they navigate away.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, variant, message }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("danger", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div
        // Bottom-start in both directions: the toast sits where the reading eye rests, and
        // logical positioning keeps it out of the sidebar in RTL and LTR alike.
        className="pointer-events-none fixed bottom-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 start-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            aria-live={toast.variant === "danger" ? "assertive" : "polite"}
            className={cn(
              "pointer-events-auto flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm shadow-lg",
              toast.variant === "success"
                ? "border-[color-mix(in_oklab,var(--success,#1f6b54)_35%,transparent)] bg-[color-mix(in_oklab,var(--success,#1f6b54)_12%,var(--surface,#ffffff))] text-[var(--foreground,#2a2523)]"
                : "border-[color-mix(in_oklab,var(--danger,#c41f3e)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger,#c41f3e)_12%,var(--surface,#ffffff))] text-[var(--foreground,#2a2523)]",
            )}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={cn(
                "mt-0.5 size-4 shrink-0",
                toast.variant === "success"
                  ? "text-[var(--success,#1f6b54)]"
                  : "text-[var(--danger,#c41f3e)]",
              )}
            >
              {toast.variant === "success" ? (
                <path d="m5 12.5 4.5 4.5L19 7.5" />
              ) : (
                <>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7.5v5M12 16h.01" />
                </>
              )}
            </svg>

            <p className="min-w-0 flex-1 leading-5">{toast.message}</p>

            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-hidden="true"
              // Hidden from assistive tech on purpose: the toast dismisses itself, and a screen
              // reader has already been handed the message by the live region.
              tabIndex={-1}
              className="-me-1 flex size-5 shrink-0 items-center justify-center rounded text-[var(--muted-foreground,#655c58)] transition-colors hover:text-[var(--foreground,#2a2523)]"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="size-3">
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used inside <ToastProvider>.");
  }
  return value;
}
